import React, { useState, useEffect } from 'react';
import {
  TrendingUp, BarChart2, PiggyBank, Wallet,
  Target, Layers, Users, BookOpen,
  Shield, Zap, ArrowRight, Check,
  Eye, EyeOff, X, LogIn, UserPlus,
  ChevronRight, Link2, Mail
} from 'lucide-react';
import { supabase, friendlyAuthError } from '../lib/supabase';

// ─── Data ─────────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: TrendingUp, color: 'blue',
    title: 'Net Worth Tracking',
    desc: 'Every account — bank, brokerage, real estate, debt — unified in one live dashboard.',
  },
  {
    icon: Wallet, color: 'emerald',
    title: 'Budget Intelligence',
    desc: 'Category-level insights, 50/30/20 analysis, and real-time spending alerts.',
  },
  {
    icon: BarChart2, color: 'violet',
    title: 'Portfolio Analytics',
    desc: 'Unified allocation across 401(k), IRA, and brokerage with risk assessment.',
  },
  {
    icon: PiggyBank, color: 'amber',
    title: 'Retirement Planning',
    desc: '500-run Monte Carlo engine projects your success rate and shows what to change.',
  },
  {
    icon: Target, color: 'rose',
    title: 'Goal Tracking',
    desc: 'Housing, emergency fund, travel, and car — with personalized savings strategies.',
  },
  {
    icon: Layers, color: 'orange',
    title: 'Equity Awards',
    desc: 'RSU vesting, NQSO/ISO tracking, ESPP optimization — all your comp, all in one view.',
  },
  {
    icon: Users, color: 'cyan',
    title: 'Family Finance',
    desc: '529 plans, UGMA accounts, college cost projections, and household goals.',
  },
  {
    icon: BookOpen, color: 'purple',
    title: 'Financial Education',
    desc: '27 in-depth articles on taxes, real estate, equity awards, and more.',
  },
];

const STEPS = [
  {
    n: '01',
    icon: Link2,
    title: 'Link your accounts',
    desc: 'Connect your banks, brokerages, 401(k)s, and credit cards in minutes using bank-level 256-bit encryption.',
  },
  {
    n: '02',
    icon: BarChart2,
    title: 'See your full picture',
    desc: 'Your complete financial life — net worth, cash flow, portfolio allocation, and retirement runway — on one screen.',
  },
  {
    n: '03',
    icon: Zap,
    title: 'Make smarter moves',
    desc: 'Proactive alerts, Monte Carlo projections, and personalized insights guide every financial decision.',
  },
];

const TESTIMONIALS = [
  {
    quote: "Finovo showed me I was 12% short of my retirement target. The Monte Carlo analysis changed how I think about saving.",
    name: 'Sarah K.',
    role: 'Software Engineer · Bay Area',
    initials: 'SK',
    grad: 'from-blue-500 to-cyan-500',
  },
  {
    quote: "Finally seeing my RSUs, 401k, and brokerage in one place made it obvious I was over-concentrated in my employer's stock.",
    name: 'Marcus T.',
    role: 'Product Manager · NYC',
    initials: 'MT',
    grad: 'from-violet-500 to-pink-500',
  },
  {
    quote: "The Learn section alone was worth it. I finally understand how depreciation works on my rental property.",
    name: 'Priya R.',
    role: 'Real Estate Investor · Austin',
    initials: 'PR',
    grad: 'from-emerald-500 to-teal-500',
  },
];

const ICON_COLORS = {
  blue:   { bg: 'bg-blue-500/15',   text: 'text-blue-400',   border: 'border-blue-500/20'   },
  emerald:{ bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  violet: { bg: 'bg-violet-500/15',  text: 'text-violet-400',  border: 'border-violet-500/20'  },
  amber:  { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/20'   },
  rose:   { bg: 'bg-rose-500/15',    text: 'text-rose-400',    border: 'border-rose-500/20'    },
  orange: { bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/20'  },
  cyan:   { bg: 'bg-cyan-500/15',    text: 'text-cyan-400',    border: 'border-cyan-500/20'    },
  purple: { bg: 'bg-purple-500/15',  text: 'text-purple-400',  border: 'border-purple-500/20'  },
};

// ─── Auth Modal ───────────────────────────────────────────────────────────────
function AuthModal({ mode, onClose, onToggleMode }) {
  const [firstName,          setFirstName]          = useState('');
  const [email,              setEmail]              = useState('');
  const [password,           setPassword]           = useState('');
  const [showPw,             setShowPw]             = useState(false);
  const [loading,            setLoading]            = useState(false);
  const [errors,             setErrors]             = useState({});
  const [authError,          setAuthError]          = useState(null);
  const [emailConfirmPending, setEmailConfirmPending] = useState(false);

  const isSignup = mode === 'signup';

  const validate = () => {
    const e = {};
    if (isSignup && !firstName.trim()) e.firstName = 'First name is required';
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = 'Valid email required';
    if (password.length < 6) e.password = 'Password must be at least 6 characters';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setAuthError(null);
    setLoading(true);
    try {
      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { first_name: firstName.trim() } },
        });
        if (error) throw error;
        // Email confirmation required — session is null
        if (data.user && !data.session) {
          setEmailConfirmPending(true);
          setLoading(false);
          return;
        }
        // Session created immediately (e.g. email confirm disabled)
        // onAuthStateChange in App.js will pick it up automatically
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // onAuthStateChange in App.js handles the transition to dashboard
      }
    } catch (err) {
      setAuthError(friendlyAuthError(err.message));
      setLoading(false);
    }
  };

  // Close on backdrop click
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdrop}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl shadow-black/60 overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-xs">F</span>
              </div>
              <span className="text-xs font-semibold text-slate-400">Finovo</span>
            </div>
            <h2 className="text-xl font-bold text-slate-100">
              {isSignup ? 'Create your account' : 'Welcome back'}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {isSignup
                ? 'Start seeing your complete financial picture'
                : 'Sign in to your financial dashboard'}
            </p>
          </div>
          <button onClick={onClose}
            className="text-slate-500 hover:text-slate-300 transition-colors p-1.5 rounded-lg hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>

        {/* Email confirm pending view */}
        {emailConfirmPending ? (
          <div className="px-6 pb-8 flex flex-col items-center text-center gap-4">
            <div className="w-14 h-14 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <Mail size={24} className="text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-1">Check your inbox</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                We sent a confirmation link to <span className="text-slate-200 font-medium">{email}</span>.
                Click it to activate your account, then come back and sign in.
              </p>
            </div>
            <button
              onClick={() => { setEmailConfirmPending(false); onToggleMode(); }}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2.5 rounded-xl transition-all text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-900/40"
            >
              <LogIn size={15} /> Go to Sign In
            </button>
            <button
              onClick={onClose}
              className="text-xs text-slate-500 hover:text-slate-400 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          {isSignup && (
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">First name</label>
              <input
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Alex"
                disabled={loading}
                className={`w-full bg-slate-800 border rounded-xl px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none transition-colors ${
                  errors.firstName ? 'border-rose-500/60 focus:border-rose-400' : 'border-slate-700 focus:border-blue-500'
                } disabled:opacity-50`}
              />
              {errors.firstName && <p className="text-xs text-rose-400 mt-1">{errors.firstName}</p>}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Email address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="alex@example.com"
              disabled={loading}
              className={`w-full bg-slate-800 border rounded-xl px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:outline-none transition-colors ${
                errors.email ? 'border-rose-500/60 focus:border-rose-400' : 'border-slate-700 focus:border-blue-500'
              } disabled:opacity-50`}
            />
            {errors.email && <p className="text-xs text-rose-400 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder={isSignup ? 'Create a password (6+ chars)' : 'Your password'}
                disabled={loading}
                className={`w-full bg-slate-800 border rounded-xl px-3.5 py-2.5 pr-10 text-sm text-slate-200 placeholder-slate-600 focus:outline-none transition-colors ${
                  errors.password ? 'border-rose-500/60 focus:border-rose-400' : 'border-slate-700 focus:border-blue-500'
                } disabled:opacity-50`}
              />
              <button type="button" onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {errors.password && <p className="text-xs text-rose-400 mt-1">{errors.password}</p>}
          </div>

          {/* Auth error banner */}
          {authError && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3 text-sm text-rose-400 flex items-start gap-2">
              <span className="mt-0.5 shrink-0">⚠️</span>
              <span>{authError}</span>
            </div>
          )}

          {/* Submit */}
          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white font-semibold py-2.5 rounded-xl transition-all text-sm flex items-center justify-center gap-2 mt-2 shadow-lg shadow-blue-900/40">
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                {isSignup ? 'Creating account…' : 'Signing in…'}
              </>
            ) : (
              <>
                {isSignup ? <UserPlus size={15} /> : <LogIn size={15} />}
                {isSignup ? 'Create Account' : 'Sign In'}
              </>
            )}
          </button>

          {/* Toggle mode */}
          <p className="text-center text-xs text-slate-500 pt-1">
            {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button type="button" onClick={onToggleMode}
              className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
              {isSignup ? 'Sign in' : 'Sign up free'}
            </button>
          </p>

          {/* Legal */}
          {isSignup && (
            <p className="text-center text-xs text-slate-600 leading-relaxed">
              By signing up you agree to our{' '}
              <span className="text-slate-500 hover:text-slate-400 cursor-pointer transition-colors">Terms of Service</span>
              {' '}and{' '}
              <span className="text-slate-500 hover:text-slate-400 cursor-pointer transition-colors">Privacy Policy</span>.
            </p>
          )}
        </form>
        )}
      </div>
    </div>
  );
}

// ─── Landing Page ─────────────────────────────────────────────────────────────
export default function Landing() {
  const [authMode,   setAuthMode]   = useState(null); // null | 'login' | 'signup'
  const [navScrolled, setNavScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 overflow-x-hidden">

      {/* ── Auth Modal ── */}
      {authMode && (
        <AuthModal
          mode={authMode}
          onClose={() => setAuthMode(null)}
          onToggleMode={() => setAuthMode(m => m === 'login' ? 'signup' : 'login')}
        />
      )}

      {/* ── Navbar ── */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        navScrolled
          ? 'bg-slate-900/95 backdrop-blur-md border-b border-slate-800 shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}>
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-900/50">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <span className="font-bold text-slate-100 text-lg tracking-tight">Finovo</span>
          </div>

          {/* Nav links */}
          <div className="hidden md:flex items-center gap-7">
            {[
              ['Features',    'features'],
              ['How It Works','how-it-works'],
              ['Education',   'education'],
            ].map(([label, id]) => (
              <button key={id} onClick={() => scrollTo(id)}
                className="text-sm text-slate-400 hover:text-slate-200 transition-colors font-medium">
                {label}
              </button>
            ))}
          </div>

          {/* Auth buttons */}
          <div className="flex items-center gap-3">
            <button onClick={() => setAuthMode('login')}
              className="hidden sm:block text-sm text-slate-300 hover:text-white font-medium transition-colors px-3 py-1.5">
              Sign In
            </button>
            <button onClick={() => setAuthMode('signup')}
              className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all shadow-lg shadow-blue-900/40 flex items-center gap-1.5">
              Get Started
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-32 pb-20 px-6 text-center overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-gradient-radial from-blue-600/12 via-violet-600/6 to-transparent rounded-full blur-3xl" />
          <div className="absolute top-20 left-1/4 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
          <div className="absolute top-32 right-1/4 w-48 h-48 bg-violet-500/5 rounded-full blur-3xl" />
        </div>

        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 text-xs font-medium text-blue-300 mb-8">
          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse" />
          Now in beta — free for early access
        </div>

        {/* Headline */}
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-6 max-w-4xl mx-auto">
          Your complete{' '}
          <span className="bg-gradient-to-r from-blue-400 via-violet-400 to-blue-400 bg-clip-text text-transparent">
            financial picture
          </span>
          ,{' '}
          <br className="hidden sm:block" />
          all in one place.
        </h1>

        {/* Subheadline */}
        <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed mb-10">
          Link your bank accounts, brokerages, 401(k)s, and more. See your net worth,
          budget, investments, and retirement runway — automatically updated, beautifully visualized.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-14">
          <button onClick={() => setAuthMode('signup')}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-3.5 rounded-xl transition-all shadow-xl shadow-blue-900/40 flex items-center justify-center gap-2 text-base">
            Get Started Free
            <ArrowRight size={17} />
          </button>
          <button onClick={() => setAuthMode('login')}
            className="w-full sm:w-auto bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 font-semibold px-8 py-3.5 rounded-xl transition-all text-base">
            Sign In
          </button>
        </div>

        {/* Stats strip */}
        <div className="inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-3 bg-slate-900/60 border border-slate-800 rounded-2xl px-8 py-4">
          {[
            ['9',    'Financial modules'],
            ['27',   'Educational articles'],
            ['500',  'Monte Carlo simulations'],
            ['Free', 'During beta'],
          ].map(([val, label]) => (
            <div key={label} className="flex items-center gap-3">
              <div>
                <div className="text-xl font-black text-slate-100 leading-none">{val}</div>
                <div className="text-xs text-slate-500 mt-0.5">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-100 mb-4">
              Everything you need to manage your financial life
            </h2>
            <p className="text-slate-400 text-lg max-w-xl mx-auto">
              Nine integrated modules covering every corner of personal finance, all talking to each other.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map(feat => {
              const c = ICON_COLORS[feat.color];
              const Icon = feat.icon;
              return (
                <div key={feat.title}
                  className="bg-slate-900 border border-slate-800 rounded-2xl p-5 hover:border-slate-700 transition-all group">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 border ${c.bg} ${c.border}`}>
                    <Icon size={18} className={c.text} />
                  </div>
                  <h3 className="text-sm font-bold text-slate-200 mb-1.5 group-hover:text-white transition-colors">
                    {feat.title}
                  </h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{feat.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-20 px-6 bg-slate-900/40 border-y border-slate-800/60">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-100 mb-4">
              Up and running in minutes
            </h2>
            <p className="text-slate-400 text-lg max-w-lg mx-auto">
              Three steps from signing up to seeing your complete financial picture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connector line — desktop only */}
            <div className="hidden md:block absolute top-12 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />

            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={step.n} className="text-center relative">
                  {/* Number bubble */}
                  <div className="relative mx-auto w-20 h-20 mb-5">
                    <div className="w-20 h-20 rounded-2xl bg-slate-900 border-2 border-blue-500/30 flex items-center justify-center mx-auto relative z-10">
                      <Icon size={26} className="text-blue-400" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-xs font-black text-white shadow-lg shadow-blue-900/60 z-20">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="text-base font-bold text-slate-200 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-100 mb-4">
              Real people, clearer finances
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map(t => (
              <div key={t.name}
                className="bg-slate-900 border border-slate-800 rounded-2xl p-6 hover:border-slate-700 transition-all">
                {/* Stars */}
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-amber-400 text-sm">★</span>
                  ))}
                </div>
                <p className="text-sm text-slate-400 leading-relaxed mb-5 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.grad} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                    {t.initials}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-200">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Education callout ── */}
      <section id="education" className="py-16 px-6 bg-slate-900/40 border-y border-slate-800/60">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-3xl mb-4">📚</div>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-100 mb-4">
            Learn as you grow
          </h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto mb-8">
            Finovo includes a built-in education hub — 27 articles across cash flow, taxes, real estate,
            equity awards, retirement, estate planning, and more. Written in plain English.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['50/30/20 Rule', 'Roth vs Traditional', 'RSUs & ISOs', '4% Rule', 'Cap Rate', 'Tax-Loss Harvesting', 'Estate Planning', 'ESPP Math'].map(tag => (
              <span key={tag}
                className="bg-slate-800 border border-slate-700 text-slate-400 text-xs font-medium px-3 py-1.5 rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Security & Trust ── */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: Shield, title: 'Bank-Level Security', desc: '256-bit AES encryption and read-only access to your financial institutions.', color: 'emerald' },
              { icon: Check,  title: 'Read-Only Access',    desc: 'Finovo never initiates transactions. We only read your data, never move it.', color: 'blue'    },
              { icon: Zap,    title: 'Real-Time Sync',      desc: 'Your financial picture updates automatically — always current, always accurate.', color: 'violet' },
            ].map(item => {
              const c = ICON_COLORS[item.color];
              const Icon = item.icon;
              return (
                <div key={item.title} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 text-center">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3 border ${c.bg} ${c.border}`}>
                    <Icon size={18} className={c.text} />
                  </div>
                  <h3 className="text-sm font-bold text-slate-200 mb-1.5">{item.title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="relative bg-gradient-to-br from-blue-600/20 via-violet-600/15 to-slate-900 border border-blue-500/20 rounded-3xl p-10 sm:p-14 text-center overflow-hidden">
            {/* Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-violet-600/5 pointer-events-none" />
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-60 h-60 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-black text-slate-100 mb-4 leading-tight">
                Start seeing your{' '}
                <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                  complete picture
                </span>
              </h2>
              <p className="text-slate-400 text-base mb-8 max-w-md mx-auto">
                Free during our beta. No credit card required. Cancel any time.
              </p>
              <button onClick={() => setAuthMode('signup')}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-10 py-3.5 rounded-xl transition-all shadow-2xl shadow-blue-900/50 text-base inline-flex items-center gap-2">
                Get Started Free
                <ArrowRight size={17} />
              </button>
              <p className="text-xs text-slate-600 mt-4">
                Already have an account?{' '}
                <button onClick={() => setAuthMode('login')} className="text-slate-400 hover:text-slate-300 transition-colors">
                  Sign in →
                </button>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-800 px-6 py-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-xs">F</span>
            </div>
            <span className="font-bold text-slate-400 text-sm">Finovo</span>
            <span className="text-slate-700 text-sm">·</span>
            <span className="text-slate-600 text-xs">Your complete financial picture</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-slate-600">
            <span className="hover:text-slate-400 cursor-pointer transition-colors">Privacy Policy</span>
            <span className="hover:text-slate-400 cursor-pointer transition-colors">Terms of Service</span>
            <span className="hover:text-slate-400 cursor-pointer transition-colors">Contact</span>
            <span>© 2026 Finovo</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
